import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import "./index-CgZzOVq6.js";
import "vue-demi";
import { u as useDataTestIdProp } from "./index-BNd8MWnO.js";
import { u as useDisabledProp } from "./index-DKxr0Yto.js";
import { u as useFullWidthProp } from "./index-4vIud5LZ.js";
import "./index-vzP2cezV.js";
import "vue-router";
import "dompurify";
import { onKeyStroke, onClickOutside } from "@vueuse/core";
import { offset, arrow as arrow$1, flip, autoUpdate, computePosition } from "@floating-ui/dom";
import { n as normalizeComponent } from "./_plugin-vue2_normalizer-VvYS6rEj.js";
function usePlacementProp() {
  const PLACEMENTS2 = [
    "top",
    "top-start",
    "top-end",
    "right",
    "right-start",
    "right-end",
    "bottom",
    "bottom-start",
    "bottom-end",
    "left",
    "left-start",
    "left-end"
  ];
  function createPlacementProp2(defaultValue = "bottom") {
    return {
      placement: {
        type: String,
        default: defaultValue,
        validator: (place) => PLACEMENTS2.includes(place)
      }
    };
  }
  return {
    PLACEMENTS: PLACEMENTS2,
    createPlacementProp: createPlacementProp2
  };
}
const { PLACEMENTS } = usePlacementProp();
function useDropdownPlacement(props) {
  const activePlacement = ref(null);
  const placementsPriorityOrder = computed(() => {
    function getMainNameOfPlacement(item) {
      return item.split("-")[0];
    }
    function isSamePlacementCategory(item) {
      return RegExp(`\\b${activePlacement.value}\\b`).test(getMainNameOfPlacement(item));
    }
    const mainPlacements = PLACEMENTS.filter((item) => isSamePlacementCategory(item));
    const restOfPlacements = PLACEMENTS.filter((item) => !isSamePlacementCategory(item));
    return [...mainPlacements, ...restOfPlacements];
  });
  function setPlacement(newPosition) {
    activePlacement.value = newPosition || "bottom";
  }
  watch(
    () => props.placement,
    () => {
      setPlacement(props.placement);
    },
    {
      deep: true,
      immediate: true
    }
  );
  return { activePlacement, placementsPriorityOrder, setPlacement };
}
function useDropdownArrow(props) {
  const arrowRef = ref(null);
  const arrowOffset = 12;
  const arrowStyles = computed(() => ({
    backgroundColor: props.arrowColor
  }));
  function roundByDPR(value) {
    const dpr = window.devicePixelRatio || 1;
    return Math.round(value * dpr) / dpr;
  }
  function updateArrowPosition(arrow2) {
    if (arrow2 && arrowRef.value) {
      const { x: arrowX, y: arrowY } = arrow2;
      Object.assign(arrowRef.value.style, {
        left: arrowX != null ? `${arrowX}px` : "",
        top: arrowY != null ? `${arrowY}px` : ""
      });
    }
  }
  return {
    arrowRef,
    arrowStyles,
    arrowOffset,
    roundByDPR,
    updateArrowPosition
  };
}
function useDropdownSetup(options) {
  const { activePlacement, placementsPriorityOrder, setPlacement, props } = options;
  const { arrowRef, arrowStyles, arrowOffset, roundByDPR, updateArrowPosition } = useDropdownArrow(props);
  const clearDropdown = ref(void 0);
  const containerRef = ref(null);
  const activatorRef = ref(null);
  const dropdownRef = ref(null);
  const currentOffset = computed(() => {
    if (!props.offset && props.showArrow) return arrowOffset;
    return props.offset;
  });
  const dropdownOptions = computed(() => ({
    strategy: "absolute",
    placement: activePlacement.value,
    middleware: [
      offset(currentOffset.value),
      arrow$1({ element: arrowRef.value }),
      flip({
        padding: currentOffset.value,
        fallbackPlacements: placementsPriorityOrder.value
      })
    ]
  }));
  function updateDropdown() {
    if (activatorRef.value && dropdownRef.value) {
      computePosition(activatorRef.value, dropdownRef.value, dropdownOptions.value).then(
        ({ x, y, placement: currentPlacement, middlewareData }) => {
          if (dropdownRef.value) {
            Object.assign(dropdownRef.value.style, {
              left: "0",
              top: "0",
              // Use Transform instead top and left to improve performance
              transform: `translate(${roundByDPR(x)}px,${roundByDPR(y)}px)`
            });
            setPlacement(currentPlacement);
          }
          updateArrowPosition(middlewareData.arrow);
        }
      );
    }
  }
  function setDropdownParent() {
    if (!props.innerDropdown) {
      document.body.appendChild(dropdownRef.value);
    }
  }
  function resetDropdownSetup() {
    if (!props.innerDropdown) {
      containerRef.value.appendChild(dropdownRef.value);
    }
    clearDropdown.value();
  }
  onMounted(() => {
    setDropdownParent();
    clearDropdown.value = autoUpdate(activatorRef.value, dropdownRef.value, updateDropdown, {
      ancestorScroll: true,
      ancestorResize: true,
      elementResize: true,
      animationFrame: false
    });
  });
  onBeforeUnmount(() => {
    resetDropdownSetup();
  });
  return {
    containerRef,
    activatorRef,
    dropdownRef,
    arrowRef,
    arrowStyles,
    updateDropdown
  };
}
function useVisibilityControl(options) {
  const { updateDropdown, emit, props } = options;
  const isDropdownVisible = ref(props.isOpen);
  function hideDropdown() {
    isDropdownVisible.value = false;
  }
  function showDropdown() {
    isDropdownVisible.value = true;
    updateDropdown();
  }
  function handleActivator() {
    if (props.disabled || props.openByHover) return;
    isDropdownVisible.value = !isDropdownVisible.value;
  }
  watch(isDropdownVisible, (isVisible) => emit(isVisible ? "focus" : "blur", isVisible));
  watch(
    () => props.isOpen,
    (newValue) => {
      isDropdownVisible.value = newValue;
    }
  );
  return {
    isDropdownVisible,
    hideDropdown,
    showDropdown,
    handleActivator
  };
}
function useDropdownActions(options) {
  const {
    containerRef,
    activatorRef,
    dropdownRef,
    isDropdownVisible,
    hideDropdown,
    showDropdown,
    emit,
    props
  } = options;
  const hoverTimeoutId = ref(false);
  function handleDropdown(show) {
    if (!show) {
      hideDropdown();
    } else {
      showDropdown();
    }
  }
  function setDropdownDelay(show) {
    if (!props.disabled) {
      clearTimeout(hoverTimeoutId.value);
      hoverTimeoutId.value = setTimeout(() => {
        handleDropdown(show);
      }, 50);
    }
  }
  function mouseEnterHandler(event) {
    if (props.openByHover) setDropdownDelay(true);
    emit("mouseenter", event);
  }
  function mouseLeaveHandler(event) {
    if (props.openByHover) setDropdownDelay(false);
    emit("mouseleave", event);
  }
  function hideOnClickAtDropdown() {
    if (props.closeOnClickContent) hideDropdown();
  }
  function hideOnKeydown(event) {
    event.preventDefault();
    hideDropdown();
  }
  if (props.allowShortcut) onKeyStroke("Escape", hideOnKeydown);
  function handleCloseDropdown(event) {
    var _a;
    if (isDropdownVisible.value && !((_a = containerRef.value) == null ? void 0 : _a.contains(event == null ? void 0 : event.target))) {
      hideDropdown();
    }
  }
  onClickOutside(dropdownRef, () => props.closeOnClickOutside && handleCloseDropdown(), {
    ignore: [activatorRef]
  });
  watch(
    () => props.disabled,
    (isDisabled) => {
      if (isDisabled) hideDropdown();
    }
  );
  return {
    setDropdownDelay,
    handleCloseDropdown,
    hideOnClickAtDropdown,
    mouseEnterHandler,
    mouseLeaveHandler
  };
}
const fadeInOutEnterActive = "_fadeInOutEnterActive_lxvag_17";
const fadeIn = "_fadeIn_lxvag_17";
const fadeInOutLeaveActive = "_fadeInOutLeaveActive_lxvag_21";
const fadeOut = "_fadeOut_lxvag_1";
const XDropdownContainer = "_XDropdownContainer_lxvag_25";
const isFullWidth = "_isFullWidth_lxvag_28";
const activator = "_activator_lxvag_31";
const dropdown = "_dropdown_lxvag_39";
const arrow = "_arrow_lxvag_39";
const arrowShape = "_arrowShape_lxvag_47";
const top = "_top_lxvag_51";
const bottom = "_bottom_lxvag_67";
const left = "_left_lxvag_83";
const right = "_right_lxvag_99";
const style0 = {
  fadeInOutEnterActive,
  fadeIn,
  fadeInOutLeaveActive,
  fadeOut,
  XDropdownContainer,
  isFullWidth,
  activator,
  dropdown,
  arrow,
  arrowShape,
  top,
  "top-start": "_top-start_lxvag_54",
  "top-end": "_top-end_lxvag_57",
  bottom,
  "bottom-start": "_bottom-start_lxvag_70",
  "bottom-end": "_bottom-end_lxvag_73",
  left,
  "left-start": "_left-start_lxvag_86",
  "left-end": "_left-end_lxvag_89",
  right,
  "right-start": "_right-start_lxvag_102",
  "right-end": "_right-end_lxvag_105"
};
const { createFullWidthProp, getFullWidthClass } = useFullWidthProp();
const { createPlacementProp } = usePlacementProp();
const { createDisabledProp } = useDisabledProp();
const { createTestIdProp, setupTestId } = useDataTestIdProp();
const __default__ = {
  name: "XDropdown",
  inheritAttrs: true
};
const _sfc_main = /* @__PURE__ */ Object.assign(__default__, {
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    offset: {
      type: Number,
      default: 0
    },
    innerDropdown: {
      type: Boolean,
      default: false
    },
    openByHover: {
      type: Boolean,
      default: false
    },
    closeOnClickContent: {
      type: Boolean,
      default: true
    },
    closeOnClickOutside: {
      type: Boolean,
      default: true
    },
    allowShortcut: {
      type: Boolean,
      default: false
    },
    showArrow: {
      type: Boolean,
      default: false
    },
    arrowColor: {
      type: String,
      default: "var(--color-neutral-0)"
    },
    dropdownClasses: {
      type: Array,
      default: () => []
    },
    disableTrasition: {
      type: Boolean,
      default: false
    },
    ...createFullWidthProp(),
    ...createPlacementProp(),
    ...createDisabledProp(),
    ...createTestIdProp()
  },
  emits: [
    "focus",
    "blur",
    "mouseenter",
    "mouseleave",
    "after:open",
    "after:close"
  ],
  setup(__props, { emit }) {
    const props = __props;
    const { formatTestId } = setupTestId(props);
    const { fullWidthClass } = getFullWidthClass(props);
    const { activePlacement, placementsPriorityOrder, setPlacement } = useDropdownPlacement(props);
    const { containerRef, activatorRef, dropdownRef, arrowRef, arrowStyles, updateDropdown } = useDropdownSetup({
      activePlacement,
      placementsPriorityOrder,
      setPlacement,
      props
    });
    const { isDropdownVisible, hideDropdown, showDropdown, handleActivator } = useVisibilityControl({
      updateDropdown,
      emit,
      props
    });
    const { handleCloseDropdown, hideOnClickAtDropdown, mouseEnterHandler, mouseLeaveHandler } = useDropdownActions({
      containerRef,
      activatorRef,
      dropdownRef,
      isDropdownVisible,
      hideDropdown,
      showDropdown,
      emit,
      props
    });
    return { __sfc: true, createFullWidthProp, getFullWidthClass, createPlacementProp, createDisabledProp, createTestIdProp, setupTestId, props, emit, formatTestId, fullWidthClass, activePlacement, placementsPriorityOrder, setPlacement, containerRef, activatorRef, dropdownRef, arrowRef, arrowStyles, updateDropdown, isDropdownVisible, hideDropdown, showDropdown, handleActivator, handleCloseDropdown, hideOnClickAtDropdown, mouseEnterHandler, mouseLeaveHandler };
  }
});
var _sfc_render = function render() {
  var _vm = this, _c = _vm._self._c, _setup = _vm._self._setupProxy;
  return _c("div", { ref: "containerRef", class: [_vm.$style.XDropdownContainer, _setup.fullWidthClass], attrs: { "data-testid": _setup.formatTestId() } }, [_c("div", { ref: "activatorRef", class: _vm.$style.activator, attrs: { "aria-disabled": _vm.disabled, "data-testid": _setup.formatTestId("activator") }, on: { "mouseenter": _setup.mouseEnterHandler, "mouseleave": _setup.mouseLeaveHandler } }, [_vm._t("activator", null, { "on": _setup.handleActivator, "isVisible": _setup.isDropdownVisible })], 2), _c("transition", { attrs: { "name": "fade", "enter-active-class": _vm.$style.fadeInOutEnterActive, "leave-active-class": _vm.$style.fadeInOutLeaveActive, "css": !_vm.disableTrasition }, on: { "after-enter": function($event) {
    return _vm.$emit("after:open", _setup.isDropdownVisible);
  }, "after-leave": function($event) {
    return _vm.$emit("after:close", _setup.isDropdownVisible);
  } } }, [_c("div", { directives: [{ name: "show", rawName: "v-show", value: _setup.isDropdownVisible, expression: "isDropdownVisible" }], ref: "dropdownRef", class: [_vm.$style.dropdown, ..._vm.dropdownClasses], attrs: { "data-testid": _setup.formatTestId("content"), "data-placement": _setup.activePlacement }, on: { "click": _setup.hideOnClickAtDropdown, "mouseenter": _setup.mouseEnterHandler, "mouseleave": _setup.mouseLeaveHandler } }, [_vm.showArrow ? _c("div", { ref: "arrowRef", class: [_vm.$style.arrow, _vm.$style[_setup.activePlacement]], attrs: { "data-testid": _setup.formatTestId("arrow") } }, [_c("div", { class: _vm.$style.arrowShape, style: _setup.arrowStyles, attrs: { "data-testid": _setup.formatTestId("arrowShape") } })]) : _vm._e(), _vm._t("default", null, { "close": _setup.handleCloseDropdown, "isVisible": _setup.isDropdownVisible })], 2)])], 1);
};
var _sfc_staticRenderFns = [];
const __cssModules = {
  "$style": style0
};
function _sfc_injectStyles(ctx) {
  for (var key in __cssModules) {
    this[key] = __cssModules[key];
  }
}
var __component__ = /* @__PURE__ */ normalizeComponent(
  _sfc_main,
  _sfc_render,
  _sfc_staticRenderFns,
  false,
  _sfc_injectStyles
);
const XDropdown = __component__.exports;
export {
  XDropdown as X,
  usePlacementProp as u
};
